// src/components/Pairing/Picture.tsx
import React, {
  FC, useCallback, useEffect, useRef, useState, memo,
} from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import ReactCardFlip from 'react-card-flip';
import useKeepOnBoard from '../../hooks/useKeepOnBoard';
import './Picture.less';

gsap.registerPlugin(Draggable);

// Jak długo obrócony obrazek pokazuje polskie znaczenie — tyle, ile w Memory widać odkrytą parę (MATCH_DELAY)
const TRANSLATION_EXPOSURE = 1200; // ms

type PictureProps = {
  id: string | number;
  link: string;
  translation: string;
  isMatched: boolean;
  isPaired: boolean;
  isMatching: boolean;
};

export const Picture: FC<PictureProps> = memo(({
  id,
  link,
  translation,
  isMatched,
  isPaired,
  isMatching,
}: PictureProps) => {
  const pictureId = `picture-${id}`;
  const draggableRef = useRef<Draggable[] | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const flipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Po zmianie rozmiaru planszy obrazek przesuwa się proporcjonalnie i nie wypada za krawędź
  useKeepOnBoard(elementRef);

  // Kliknięcie albo tapnięcie obraca obrazek na stronę z polskim znaczeniem, po chwili wraca. Woła je Draggable
  // tylko wtedy, gdy obrazka nie przesunięto, więc przytrzymanie i przeciąganie go nie obraca. Kliknięcia
  // w trakcie pokazywania nic nie zmieniają
  const showTranslation = useCallback(() => {
    if (flipTimerRef.current) return;

    setIsFlipped(true);
    flipTimerRef.current = setTimeout(() => {
      flipTimerRef.current = null;
      setIsFlipped(false);
    }, TRANSLATION_EXPOSURE);
  }, []);

  useEffect(() => () => {
    if (flipTimerRef.current) clearTimeout(flipTimerRef.current);
  }, []);

  useEffect(() => {
    const pictureElement = elementRef.current;
    if (!pictureElement || isPaired || initializedRef.current) {
      return undefined;
    }

    setTimeout(() => {
      const boardElement = document.querySelector('.boardForPics');
      if (!boardElement) return;

      const boardRect = boardElement.getBoundingClientRect();
      const maxX = boardRect.width - pictureElement.offsetWidth - 20;
      const maxY = boardRect.height - pictureElement.offsetHeight - 20;

      const randomX = 20 + Math.random() * (maxX - 20);
      const randomY = 20 + Math.random() * (maxY - 20);

      const centerX = boardRect.width / 2 - pictureElement.offsetWidth / 2;
      const centerY = boardRect.height / 2 - pictureElement.offsetHeight / 2;

      // 🎯 Ustaw style PRZED dodaniem klasy
      pictureElement.style.position = 'absolute';
      pictureElement.style.left = `${centerX}px`;
      pictureElement.style.top = `${centerY}px`;
      pictureElement.style.transform = 'scale(0.3) rotate(0deg)';

      // 🎯 Dodaj klasę, żeby CSS przestał ukrywać
      pictureElement.classList.add('initialized');

      // 🎯 ANIMACJA ROZRZUCENIA
      gsap.to(pictureElement, {
        left: randomX,
        top: randomY,
        opacity: 1,
        scale: 1,
        rotation: () => gsap.utils.random(-5, 5),
        duration: 0.8,
        delay: Math.random() * 0.4,
        ease: 'back.out(1.4)',
      });

      gsap.delayedCall(1.2, () => {
        draggableRef.current = Draggable.create(`#${pictureId}`, {
          type: 'x,y',
          edgeResistance: 0.65,
          onClick: showTranslation,
        });
      });

      initializedRef.current = true;
    }, 100);

    return () => {
      if (draggableRef.current) {
        draggableRef.current.forEach((instance) => instance.kill());
        draggableRef.current = null;
      }
    };
  }, [pictureId, isPaired, id, showTranslation]);

  // ✅ ZIELONY BORDER – trafiony obrazek stoi w miejscu, dopóki lektor czyta
  useEffect(() => {
    if (!isMatched || !elementRef.current) {
      return undefined;
    }

    const element = elementRef.current;

    element.style.border = '5px solid green';
    element.style.boxShadow = '0 0 4px rgba(0, 255, 0, 0.8)';

    if (draggableRef.current) {
      draggableRef.current.forEach((instance) => instance.disable());
    }

    return undefined;
  }, [isMatched]);

  // ✅ SHRINK
  useEffect(() => {
    if (!isMatching || !elementRef.current) {
      return undefined;
    }

    const element = elementRef.current;

    gsap.to(element, {
      scale: 0,
      rotation: 480,
      opacity: 0,
      duration: 0.5,
      ease: 'back.in(1.2)',
      onComplete: () => {
        element.style.boxShadow = '';
      },
    });

    return undefined;
  }, [isMatching]);

  if (isPaired) return null;

  return (
    <div
      ref={elementRef}
      id={pictureId}
      className="picture"
    >
      <ReactCardFlip
        containerClassName="picture_flip"
        isFlipped={isFlipped}
        flipDirection="vertical"
        flipSpeedFrontToBack={0.2}
        flipSpeedBackToFront={0.2}
      >
        <div className="picture_front">
          <img alt={translation} src={link} />
        </div>
        <div className="picture_back">
          <span className="picture_translation">{translation}</span>
        </div>
      </ReactCardFlip>
    </div>
  );
});

export default Picture;

// // src/components/Pairing/Picture.tsx
// import React, { FC, useEffect } from 'react';
// import './Picture.less';
// import { gsap } from 'gsap';
// import { Draggable } from 'gsap/Draggable';

// gsap.registerPlugin(Draggable);

// type PictureProps = {
//   id: string;
//   link: string;
//   translation: string;
// };

// export const Picture: FC<PictureProps> = function Picture({
//   id, link, translation,
// }: any) {
//   const pictureId = `picture-${id}`;

//   useEffect(() => {
//     const pictureElement = document.getElementById(pictureId);

//     if (pictureElement && !Draggable.get(`#${pictureId}`)) {
//       const maxX = window.innerWidth - 200 - pictureElement.offsetWidth;
//       const maxY = window.innerHeight - 200 - pictureElement.offsetHeight;

//       const randomX = Math.random() * maxX;
//       const randomY = Math.random() * maxY;

//       pictureElement.style.position = 'absolute';
//       pictureElement.style.left = `${randomX}px`;
//       pictureElement.style.top = `${randomY}px`;

//       Draggable.create(`#${pictureId}`, {
//         type: 'x,y',
//         bounds: '.boardForPics',
//         throwProps: true,
//       });
//     }
//   }, [pictureId]);

//   return (
//     <>
//       <div
//         id={pictureId}
//         className="picture"
//       >
//         <img alt={translation} src={link} />
//       </div>
//     </>
//   );
// };
